import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { uid } from '../../../../lib/uid';
import type { RecruitmentUserOption } from '../../vacancies/vacancyTypes';
import {
  APPLICANT_ASSESSMENT_RESULT,
  APPLICANT_QUALIFICATION,
  type ApplicantAssessmentResultValue,
  type ApplicantQualificationValue,
  type ApplicantRequirementRequest,
} from '../applicantTypes';

/*
  Autogenerate for the recruitment cycle's forms. Synthetic values only; every foreign key is
  resolved against the LIVE list the form already loaded, so a generator handed nothing fills
  nothing rather than inventing an id.
*/

const REQUIREMENTS = [
  { name: 'Personal Data Sheet', description: 'CS Form 212, with a recent photograph.' },
  { name: 'Transcript of Records', description: 'Authenticated copy.' },
  { name: 'Certificate of Eligibility', description: 'Or a board rating, where the position requires one.' },
  { name: 'Performance Rating', description: 'Latest rating period, for applicants already in government.' },
  { name: 'Training Certificates', description: 'Relevant to the position applied for.' },
];

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

/** A requirement, suffixed so two clicks never collide on the live-name index. */
export function sampleApplicantRequirement(): ApplicantRequirementRequest {
  const requirement = pick(REQUIREMENTS);
  return {
    name: `${requirement.name} ${uid().slice(0, 6).toUpperCase()}`,
    description: requirement.description,
    requiresAttachment: Math.random() < 0.5,
  };
}

/**
 * A screening: an outcome, a screener from the live accounts, today, a note — and a synthetic name
 * for any checklist line the reader added and left blank. The Complied? answers are the form's own.
 */
export function sampleScreening(users: RecruitmentUserOption[]) {
  const result: ApplicantQualificationValue =
    Math.random() < 0.75 ? APPLICANT_QUALIFICATION.Qualified : APPLICANT_QUALIFICATION.Disqualified;

  return {
    result,
    screenedByUserId: users.length > 0 ? pick(users).userId : null,
    screenedDate: todayCalendarDate(),
    remarks: result === APPLICANT_QUALIFICATION.Qualified ? 'Documents complete.' : 'Does not meet the education standard.',
    requirementName: () => sampleApplicantRequirement().name,
  };
}

/**
 * An assessment stage: a result and a short synthetic note as a DOCUMENT — the form remounts the
 * editor to take it. Files are NOT generated: the server verifies them by magic bytes.
 */
export function sampleAssessment(stageLabel: string) {
  const result: ApplicantAssessmentResultValue =
    Math.random() < 0.75 ? APPLICANT_ASSESSMENT_RESULT.Passed : APPLICANT_ASSESSMENT_RESULT.Failed;
  const note = result === APPLICANT_ASSESSMENT_RESULT.Passed ? 'Meets the standard for the post.' : 'Below the standard for the post.';

  return {
    result,
    remarks: `<p><strong>${stageLabel}</strong> — ${note}</p><ul><li>Assessor notes on file.</li><li>Sample ${uid().slice(0, 6).toUpperCase()}</li></ul>`,
  };
}

/** Five weights adding up to exactly 100, in whole and half points. */
export function sampleScoreWeights(count: number): number[] {
  const halves = Array.from({ length: count }, () => 1 + Math.floor(Math.random() * 8));
  const total = halves.reduce((sum, h) => sum + h, 0);
  const weights = halves.map((h) => Math.round(((h / total) * 100) * 2) / 2);
  // The rounding remainder goes on the last stage, so the total is exact.
  weights[count - 1] = Math.round((100 - weights.slice(0, -1).reduce((sum, w) => sum + w, 0)) * 100) / 100;
  return weights;
}

/** An interview a week out, mid-morning, in a synthetic room. */
export function sampleInterview() {
  const day = new Date();
  day.setDate(day.getDate() + 7);
  const date = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;

  return {
    place: `Conference Room ${pick(['A', 'B', 'C'])}`,
    date,
    time: pick(['09:00', '09:30', '10:00', '13:30', '14:00']),
  };
}
